import { useCallback, useEffect, useState } from "react";
import { useFileUploadProfiles } from "@components/FileUpload/FileUpload.constants";
import SingleFile from "@components/FileUpload/SingleFile";
import { useUploadStore, type UploadEntry } from "@components/FileUpload/uploadStore";
import FormItem from "@components/FormItem/FormItem";
import CustomFormLabel from "@components/FormItem/FormItem.styles";
import SideDrawer from "@components/SimpleSideDrawer/SideDrawer";
import { useNotification } from "@context/NotificationContext";
import { useCompleteDepartureTask } from "@features/vessel-call/vessel-call-status/hooks/useCompleteDepartureTask";
import { zodResolver } from "@hookform/resolvers/zod";
import { uploadFileToAzureBlob } from "@services/azureDataApi";
import { SasTokenPrefix, SasTokenPrivilege } from "@services/user/types";
import { getSasToken } from "@services/userApi";
import { useMutation } from "@tanstack/react-query";
import { runTransaction } from "@telemetry/runTransaction";
import type { TFunction } from "i18next";
import { FormProvider, useForm } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { z } from "zod";
import { useClearanceTypeQuery } from "../../hooks/useClearanceTypeQuery";
import { LANG_BASE, TEST_ID_BASE } from "../DepartureStatus/DepartureStatus";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  onError: () => void;
  taskId: string;
  hasCancelledDeparture: boolean;
  requestAnchorageClearance: boolean;
};

const INTERNAL_ATTACHMENT_KEY = "internalApprovalAttachment";

const documentSchema = z.object({
  name: z.string(),
  url: z.string(),
  type: z.string(),
});

const getApproveSchema = (t: TFunction) =>
  z.object({
    departureClearance: z.boolean().nullish(),
    internalNotes: z
      .string()
      .max(
        500,
        t("form-validation.max-length", { label: t(`${LANG_BASE}.internal-notes`), max: 500 }),
      )
      .optional(),
    [INTERNAL_ATTACHMENT_KEY]: z.array(documentSchema).optional(),
  });

const ApproveSideDrawer = ({
  isOpen,
  onClose,
  onSuccess,
  onError,
  taskId,
  hasCancelledDeparture,
  requestAnchorageClearance,
}: Props) => {
  const { t } = useTranslation();
  const { notify } = useNotification();

  const { id: vesselCallId } = useParams();
  const { data: clearanceData } = useClearanceTypeQuery(vesselCallId);

  const shouldDisplayAnchorageCheckbox = Boolean(
    clearanceData?.hasVisitedAnchorageOnly &&
      clearanceData?.clearanceType === "ANCHORAGE_CLEARANCE",
  );

  const shoudHideCheckbox = Boolean(
    clearanceData?.hasVisitedAnchorageOnly && clearanceData?.clearanceType === "PORT_CLEARANCE",
  );

  const shouldShowClearanceCheckbox = Boolean(
    (clearanceData?.displayRequestClearanceCheckbox && !shoudHideCheckbox) ||
      shouldDisplayAnchorageCheckbox,
  );

  const needsMarineDecision =
    (shouldDisplayAnchorageCheckbox || clearanceData?.displayRequestClearanceCheckbox) &&
    !hasCancelledDeparture;

  const shouldShowRequestedByAgent = Boolean(
    requestAnchorageClearance && (hasCancelledDeparture || needsMarineDecision),
  );

  const getClearanceText = (
    clearanceType: string | undefined,
  ): { description: string; checkboxLabel: string } => {
    if (clearanceType === "PORT_CLEARANCE")
      return {
        description: hasCancelledDeparture
          ? `${LANG_BASE}.requested-new-port-clearance`
          : `${LANG_BASE}.requested-port-clearance`,
        checkboxLabel: hasCancelledDeparture
          ? `${LANG_BASE}.new-port-clearance-checkbox`
          : `${LANG_BASE}.port-clearance-checkbox`,
      };
    return {
      description: hasCancelledDeparture
        ? `${LANG_BASE}.requested-new-anchorage-clearance`
        : `${LANG_BASE}.requested-anchorage-clearance`,
      checkboxLabel: hasCancelledDeparture
        ? `${LANG_BASE}.new-anchorage-clearance-checkbox`
        : `${LANG_BASE}.anchorage-clearance-checkbox`,
    };
  };

  const { description, checkboxLabel } = getClearanceText(clearanceData?.clearanceType);

  const FILE_UPLOAD_PROFILES = useFileUploadProfiles();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { clearFilesForKey, setFilesForKey, filesByKey } = useUploadStore();

  const internalFile = filesByKey[INTERNAL_ATTACHMENT_KEY] || [];

  const profile = FILE_UPLOAD_PROFILES.sideDrawerApprovalFile;
  const allowedSideDrawerExtensions = profile.format.map((ext) =>
    ext.startsWith(".") ? ext.toLowerCase() : `.${ext.toLowerCase()}`,
  );

  const approveSchema = getApproveSchema(t);
  type ApproveFormValues = z.infer<typeof approveSchema>;

  const form = useForm<ApproveFormValues>({
    defaultValues: {
      departureClearance: false,
    },
    mode: "onTouched",
    reValidateMode: "onChange",
    resolver: zodResolver(approveSchema),
    shouldUnregister: true,
  });

  const handleClose = () => {
    form.reset();
    clearFilesForKey(INTERNAL_ATTACHMENT_KEY);
    onClose();
  };

  const { completeTask } = useCompleteDepartureTask({
    handleSuccess: () => {
      handleClose();
      onSuccess();
    },
    handleError: (error) => {
      notify({
        title: t("common.error"),
        description:
          error?.response?.data?.detail ?? error?.message ?? t("common.something-went-wrong"),
        type: "error",
      });
      handleClose();
      onError();
    },
  });

  const onValid = async (data: ApproveFormValues) => {
    const { internalNotes, internalApprovalAttachment, departureClearance } = data;
    const attachment = internalApprovalAttachment?.[0];

    try {
      setIsSubmitting(true);
      await completeTask({
        taskId,
        params: {
          status: "APPROVED",
          requestDepartureClearance: departureClearance,
          internalNotes: internalNotes,
          internalAttachmentsName: attachment?.name,
          internalAttachmentsUrl: attachment?.url,
        },
      });
    } catch (error) {
      console.log(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const onSubmit = () => {
    form.handleSubmit(onValid)();
  };

  useEffect(() => {
    clearFilesForKey(INTERNAL_ATTACHMENT_KEY);
  }, []);

  const mutationFn = ({ file, documentType }: { file: File; documentType: string }) =>
    runTransaction(
      {
        module: "vessel_call",
        screen: "ApproveDeparture",
        action: "upload_departure_document",
        entityType: "departure_document",
      },
      async (transactionId) => {
        const payloadForSasToken = {
          prefix: SasTokenPrefix.VESSEL_CALL,
          contentType: file.type || null,
          privilege: SasTokenPrivilege.WRITE,
          blobUrl: null,
        };

        const { blobUrl, sasToken } = await getSasToken(
          payloadForSasToken,
          ...(transactionId ? [transactionId] : []),
        );

        const [uploadResult] = await uploadFileToAzureBlob({
          file,
          blobUrl,
          sasToken,
          certificateType: documentType,
        });

        const documentUrl = uploadResult?.documentUrl ?? blobUrl;
        return { documentUrl, documentType };
      },
    );

  const internalDocumentUpload = useMutation({ mutationFn });

  const handleInternalDocumentUpload = useCallback(
    (files: File[]) => {
      const file = files[0];
      if (!file) return;

      internalDocumentUpload.mutate(
        { file, documentType: "INTERNAL_NOTES" },
        {
          onSuccess: ({ documentUrl }) => {
            form.setValue(INTERNAL_ATTACHMENT_KEY, [
              { url: documentUrl, name: file.name, type: file.type },
            ]);
            setFilesForKey(INTERNAL_ATTACHMENT_KEY, [
              {
                name: file.name,
                url: documentUrl,
              },
            ]);
          },
          onError: () => {
            clearFilesForKey(INTERNAL_ATTACHMENT_KEY);
            notify({
              title: t("vessel-management.notifications.attachments-upload-error"),
              description: t(
                "vessel-management.notifications.attachments-upload-error-description",
              ),
              type: "error",
            });
          },
        },
      );
    },
    [clearFilesForKey, internalDocumentUpload, form, notify, setFilesForKey, t],
  );
  const handleRemoveFile = useCallback(() => {
    form.setValue(INTERNAL_ATTACHMENT_KEY, undefined);
    clearFilesForKey(INTERNAL_ATTACHMENT_KEY);
  }, [clearFilesForKey, form]);

  return (
    <div data-testid="departure-approve-sidedrawer">
      <FormProvider {...form}>
        <SideDrawer
          isOpen={isOpen}
          onClose={handleClose}
          onSubmit={onSubmit}
          isSubmitting={isSubmitting}
          keepMounted={false}
          title={t(`${LANG_BASE}.approve-title`)}
          description={
            shouldShowRequestedByAgent ? (
              <span className="block w-[352px] max-w-full">
                <Trans i18nKey={description} components={{ strong: <strong /> }} />
              </span>
            ) : (
              t(`${LANG_BASE}.approve-description`)
            )
          }
          submitLabel={t(`${LANG_BASE}.approve-button`)}
        >
          <div className="w-[400px] p-6 flex flex-col gap-6">
            {(shouldShowClearanceCheckbox || needsMarineDecision || hasCancelledDeparture) && (
              <>
                <FormItem
                  name="departureClearance"
                  type="checkbox"
                  label={t(checkboxLabel)}
                  testId={`${TEST_ID_BASE}-clearance-checkbox`}
                />
              </>
            )}
            <FormItem
              name="internalNotes"
              type="textarea"
              label={t(`${LANG_BASE}.internal-notes`)}
              testId={`${TEST_ID_BASE}-internal-notes`}
              minRows={4}
              maxChars={500}
            />
            {internalFile[0] ? (
              <div className="MuiFormControl-root MuiFormControl-fullWidth css-kjxwpk-MuiFormControl-root">
                <CustomFormLabel sx={{ mb: 1 }}>
                  {t(`${LANG_BASE}.internal-document`)}
                </CustomFormLabel>
                <SingleFile
                  testId={`${TEST_ID_BASE}-internal-document-0`}
                  file={internalFile[0]}
                  fieldName={internalFile[0].name}
                  currentFileIndex={0}
                  setFiles={handleRemoveFile}
                  isLoading={internalDocumentUpload.isPending}
                />
              </div>
            ) : (
              <FormItem
                name={INTERNAL_ATTACHMENT_KEY}
                type="file"
                label={t(`${LANG_BASE}.internal-document`)}
                testId={`${TEST_ID_BASE}-internal-document`}
                allowedFileExtensions={allowedSideDrawerExtensions}
                uploadConditions={profile}
                maxFiles={1}
                onUpload={(_, files) => handleInternalDocumentUpload(files)}
                setFiles={(next) => setFilesForKey(INTERNAL_ATTACHMENT_KEY, next as UploadEntry[])}
                files={internalFile}
              />
            )}
          </div>
        </SideDrawer>
      </FormProvider>
    </div>
  );
};

export default ApproveSideDrawer;
